import { navigate } from './calendar-spa.js';

// 업무함 (spec §4.1·§4.3). 필터는 "적용" 없이 navigate() 로 즉시 반영되고,
// 루트 #workHub 는 REFRESH_ROOTS 에 있으므로 서버가 그린 섹션이 그대로 갈린다.
// 인라인 행동은 **새 엔드포인트를 만들지 않고** 각 모듈의 기존 POST 로 간다.

const CSRF = () => document.querySelector('meta[name=csrf-token]')?.content || '';

// 소스별 계약. 파라미터 이름은 각 엔드포인트의 현재 계약 그대로다 (§4.3):
// 사내 업무 요청만 message, 나머지는 note. ERP 는 approval_id + 문서 버전.
const ENDPOINTS = {
    work: {
        url: (li) => `/api/work/requests/${li.dataset.id}`,
        body: (li, act, reason) => ({ action: act === 'complete' ? 'submit' : act, if_version: li.dataset.version, message: reason }),
    },
    approval: {
        url: (li, act) => `/approval/docs/${li.dataset.id}/${act}`,
        body: (li, act, reason) => ({ note: reason }),
    },
    attendance: {
        url: (li, act) => `/attendance/approvals/${li.dataset.id}/${act}`,
        body: (li, act, reason) => ({ note: reason }),
    },
    erp: {
        url: (li) => `/api/erp/docs/${li.dataset.id}/approval`,
        body: (li, act, reason) => ({ action: act, approval_id: li.dataset.actionId, if_version: li.dataset.version, note: reason }),
    },
};
// 사유(또는 처리 결과)를 먼저 받아야 하는 행동. submit(검토 요청)과 complete(완료)는
// 둘 다 서버의 submit 액션이다 — 라벨만 승인자 유무로 갈린다 (§5.1).
const NEEDS_REASON = { reject: 'reason', complete: 'resolution', submit: 'resolution' };
// 행동별 토스트: 없는 행동은 공통 "처리했습니다".
const DONE_LABEL = { submit: 'submitDone', complete: 'completeDone' };

function labels() {
    try { return JSON.parse(document.getElementById('workLabels')?.textContent || '{}'); } catch (_) { return {}; }
}

function toast(text) {
    const box = document.getElementById('workFeedback');
    if (!box || !text) return;
    box.classList.remove('error');
    box.textContent = text;
    box.hidden = false;
}

function fail(note, text, href, openLabel) {
    note.textContent = text || '';
    note.hidden = false;
    if (href) {
        const link = document.createElement('a');
        link.href = href;
        link.className = 'btn btn-ghost';
        link.textContent = openLabel || href;
        note.appendChild(link);
    }
}

// 409 는 덮어쓰지 않는다 — 새로고침만 권한다 (§4.3).
function conflict(note, text, refreshLabel) {
    fail(note, text);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-ghost';
    button.textContent = refreshLabel;
    button.addEventListener('click', () => navigate(location.href, { replace: true, keepScroll: true }));
    note.appendChild(button);
}

function removeRow(li, text) {
    const section = li.closest('.work-section');
    li.remove();
    if (section) {
        const count = section.querySelector('[data-section-count]');
        const left = section.querySelectorAll('.work-item').length;
        if (count) count.textContent = String(left);
        if (!left && !section.querySelector('.work-section-empty')) {
            const p = document.createElement('p');
            p.className = 'text-muted work-section-empty';
            p.textContent = section.querySelector('h2')?.dataset.empty || '';
            section.appendChild(p);
        }
        if (section.dataset.section !== 'mine') {
            const badge = document.getElementById('navcount-home-work');
            if (badge) {
                const next = Math.max(0, (parseInt(badge.textContent, 10) || 0) - 1);
                badge.textContent = String(next);
                badge.hidden = next === 0;
            }
        }
    }
    toast(text);
}

// 업무함(#workHub)과 업무 요청 목록(#workList, §5.2)이 같은 즉시 필터를 쓴다:
// 검색은 250 ms 디바운스, 선택·날짜는 즉시 navigate(). 목록은 replace+keepScroll 이라
// 필터 한 글자마다 히스토리를 쌓지 않는다; 업무함은 예전대로 push 다.
const FILTER_FORMS = '[data-work-filter],[data-work-list-filter]';

export function setupWorkInbox() {
    const host = document.getElementById('workMain');
    if (!host || !(document.getElementById('workHub') || document.getElementById('workList')) || host.dataset.inboxBound) return;
    host.dataset.inboxBound = '1';
    const L = labels();
    let timer = 0;
    const go = (form) => {
        const url = new URL(form.getAttribute('action') || '/work', location.origin);
        new FormData(form).forEach((value, key) => { if (String(value).trim()) url.searchParams.set(key, value); });
        if (form.hasAttribute('data-work-list-filter')) navigate(url.pathname + url.search, { replace: true, keepScroll: true });
        else navigate(url.pathname + url.search);
    };
    // 250 ms 안에 탭·기간 링크를 누르거나 Enter 를 치면 낡은 폼이 나중에 (replace 로)
    // 다시 이동하던 것: 링크 클릭·제출·SPA 이동 완료에 타이머를 끊고, 타이머가 울릴
    // 때 폼이 이미 갈렸으면(!isConnected) 버린다.
    const cancelPending = () => { clearTimeout(timer); timer = 0; };
    host.addEventListener('input', (event) => {
        const form = event.target.closest(FILTER_FORMS);
        if (!form || event.target.type !== 'search') return;
        clearTimeout(timer); timer = setTimeout(() => { timer = 0; if (form.isConnected) go(form); }, 250);
    });
    host.addEventListener('click', (event) => { if (event.target.closest('a[href]')) cancelPending(); }, true);
    document.addEventListener('calendar:navigated', cancelPending);
    document.addEventListener('calendar:page-swapped', cancelPending);
    host.addEventListener('change', (event) => {
        const form = event.target.closest(FILTER_FORMS);
        if (form && (event.target.tagName === 'SELECT' || event.target.type === 'date')) go(form);
    });

    const run = async (li, act, reason) => {
        const adapter = ENDPOINTS[li.dataset.source];
        if (!adapter) return;
        const buttons = [...li.querySelectorAll('button')];
        buttons.forEach((b) => { b.disabled = true; });
        li.setAttribute('aria-busy', 'true');
        const note = li.querySelector('.work-item-feedback');
        note.hidden = true; note.textContent = '';
        try {
            const body = new URLSearchParams();
            Object.entries(adapter.body(li, act, reason || '')).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') body.set(k, v); });
            const response = await fetch(adapter.url(li, act), {
                method: 'POST', body,
                headers: { Accept: 'application/json', 'X-CSRF-Token': CSRF(), 'X-Work-Inline': '1' },
            });
            if (response.ok) { removeRow(li, L[DONE_LABEL[act]] || L.actionDone); return; }
            const data = await response.json().catch(() => ({}));
            if (response.status === 403) fail(note, data.message || L.forbidden);
            else if (response.status === 409) conflict(note, data.message || L.conflict, L.refresh);
            // 400 + url: 요청 자체의 문제(체크리스트 미완·승인자 불가) — 메시지 + 「상세에서 확인」 (§5.1).
            else if (response.status === 400 && data.url) fail(note, data.message || L.failed, data.url, L.openDetail);
            else fail(note, data.message || L.failed, li.querySelector('a.work-row')?.getAttribute('href'), L.open);
        } catch (_) {
            fail(note, L.failed);
        } finally {
            buttons.forEach((b) => { b.disabled = false; });
            li.removeAttribute('aria-busy');
        }
    };

    // 사유/처리 결과 입력은 행 안에서 펼친다 — 페이지를 떠나지 않는다.
    const askReason = (li, act) => {
        li.querySelector('.work-item-reason')?.remove();
        const form = document.createElement('form');
        form.className = 'work-item-reason';
        const label = document.createElement('label');
        label.textContent = (NEEDS_REASON[act] === 'resolution' ? L.resolution : L.reason) || '';
        const input = document.createElement('input');
        input.className = 'form-input';
        input.required = true;
        input.maxLength = 500;
        label.appendChild(input);
        const ok = document.createElement('button');
        ok.className = 'btn btn-primary';
        ok.textContent = L.confirm || '';
        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'btn btn-ghost';
        cancel.textContent = L.cancel || '';
        cancel.addEventListener('click', () => form.remove());
        form.append(label, ok, cancel);
        form.addEventListener('submit', (event) => {
            event.preventDefault();
            const value = input.value.trim();
            if (!value) { input.setCustomValidity(L.reasonRequired || ' '); input.reportValidity(); return; }
            input.setCustomValidity('');
            form.remove();
            run(li, act, value);
        });
        input.addEventListener('input', () => input.setCustomValidity(''));
        li.appendChild(form);
        input.focus();
    };

    host.addEventListener('click', (event) => {
        if (event.target.closest('[data-notif-open]')) { document.getElementById('notifBell')?.click(); return; }
        const button = event.target.closest('button.work-act');
        if (!button) return;
        const li = button.closest('.work-item');
        const act = button.dataset.act;
        if (!li || !act) return;
        event.preventDefault();
        if (NEEDS_REASON[act]) askReason(li, act);
        else run(li, act, '');
    });

    host.addEventListener('submit', (event) => {
        const form = event.target.closest(FILTER_FORMS);
        if (form) { event.preventDefault(); cancelPending(); go(form); }
    });
}
